// Copyright © 2025 Broadcom Inc. and its subsidiaries. All Rights Reserved.
import React, { Fragment } from "react";
import { useIntl } from "react-intl";
import { compose } from "recompose";
import {
  Button,
  Checkbox,
  Collapse,
  Divider,
  FormControlLabel,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Typography,
} from "@material-ui/core";
import FiberManualRecordSharpIcon from '@material-ui/icons/FiberManualRecordSharp';
import { bool, func, object } from "prop-types";

import { FormSelect, FormTextField, Loading, Switch } from '../../../components';

import { getI18n, getI18nFormattedMessage } from "../../../utils/intl";
import { get } from "lodash";

const DAILY = 'DAILY';
const WEEKLY = 'WEEKLY';
const BIWEEKLY = 'BIWEEKLY';
const MONTHLY = 'MONTHLY';

const KEY_EXPIRY_REPORT_CADENCE = [{
    uuid: DAILY,
    text: getI18nFormattedMessage('label.settings.application.keysettings.key.expiry.reports.report.cadence.daily'),
  }, {
    uuid: WEEKLY,
    text: getI18nFormattedMessage('label.settings.application.keysettings.key.expiry.reports.report.cadence.weekly'),
  }, {
    uuid: BIWEEKLY,
    text: getI18nFormattedMessage('label.settings.application.keysettings.key.expiry.reports.report.cadence.biweekly'),
  }, {
    uuid: MONTHLY,
    text: getI18nFormattedMessage('label.settings.application.keysettings.key.expiry.reports.report.cadence.monthly'),
  }];

const MON_DAY = 'MONDAY';
const TUES_DAY = 'TUESDAY';
const WEDNES_DAY = 'WEDNESDAY';
const THURS_DAY = 'THURSDAY';
const FRI_DAY = 'FRIDAY';
const SATUR_DAY = 'SATURDAY';
const SUN_DAY = 'SUNDAY';

const WEEK_REPORT_DAY_OPTIONS = [{
    uuid: MON_DAY,
    text: getI18nFormattedMessage('label.settings.application.keysettings.key.expiry.reports.report.day.week.options.monday'),
  },
  {
    uuid: TUES_DAY,
    text: getI18nFormattedMessage('label.settings.application.keysettings.key.expiry.reports.report.day.week.options.tuesday'),
  },
  {
    uuid: WEDNES_DAY,
    text: getI18nFormattedMessage('label.settings.application.keysettings.key.expiry.reports.report.day.week.options.wednesday'),
  },
  {
    uuid: THURS_DAY,
    text: getI18nFormattedMessage('label.settings.application.keysettings.key.expiry.reports.report.day.week.options.thursday'),
  },
  {
    uuid: FRI_DAY,
    text: getI18nFormattedMessage('label.settings.application.keysettings.key.expiry.reports.report.day.week.options.friday'),
  },
  {
    uuid: SATUR_DAY,
    text: getI18nFormattedMessage('label.settings.application.keysettings.key.expiry.reports.report.day.week.options.saturday'),
  },
  {
    uuid: SUN_DAY,
    text: getI18nFormattedMessage('label.settings.application.keysettings.key.expiry.reports.report.day.week.options.sunday'),
  }];

const FIRST_DAY = 'FIRST';
const FIFTEENTH_DAY = 'FIFTEENTH';
const LAST_DAY = 'LAST';

const MONTH_REPORT_DAY_OPTIONS = [{
    uuid: FIRST_DAY,
    text: getI18nFormattedMessage('label.settings.application.keysettings.key.expiry.reports.report.day.month.options.firstday'),
  },
  {
    uuid: FIFTEENTH_DAY,
    text: getI18nFormattedMessage('label.settings.application.keysettings.key.expiry.reports.report.day.month.options.fifteenthday'),
  },
  {
    uuid: LAST_DAY,
    text: getI18nFormattedMessage('label.settings.application.keysettings.key.expiry.reports.report.day.month.options.lastday'),
  }];

const preventSignsAndFraction = event => {
  if (event.key === '-' || event.key === '+' || event.key === '.') {
    event.preventDefault();
  }
};

export const ExpiryReportingSettings = (props) => {
  const {
    classes,
    executeExpiryReportingJob,
    isExecutingExpiryJob,
    isExecutingReportingJob,
    isLoading,
    urgentReportingError,
    startReportingError,
    formValues,
    setFormValues,
    isFormDirty,
    handleInputChange,
  } = props;
  const intl = getI18n(useIntl());
  const {
    notificationsEnabled,
    reportCadence,
    reportDay,
    sendReportToOrgPublisher,
    sendReportToAdmin,
    startReporting,
    urgentReporting,
    reportIncludes,
    expireKeys,
  } = formValues;

 const handleCadenceChange = (value) => {
  let reportDayValue = MON_DAY;
  if (value === DAILY) {
    reportDayValue = '';
  } else if (value === MONTHLY) {
    reportDayValue = FIRST_DAY;
  } else {
    reportDayValue = MON_DAY;
  }
  setFormValues({
    ...formValues,
    reportCadence: value,
    reportDay: reportDayValue,
  })
 };
 const handleNumberFieldChange = (name, value) => {
  if(value < 2147483647) { //Int max range
    handleInputChange({ target: { name, value } });
  }
}
  const handleReportIncludesChange = (authMethod, checked) => {
    setFormValues({
      ...formValues,
      reportIncludes: {
        ...reportIncludes,
        [authMethod]: checked,
      },
    });
  }

  const renderStartCategory = () =>
    (<Fragment>
      <div>
        <b>
          {intl.getI18nMessage("label.settings.application.keysettings.key.expiry.status.categories.start.expiry.field.label")}
        </b>
        &nbsp;
      </div>
      <div>{intl.getI18nMessage("label.settings.application.keysettings.key.expiry.status.categories.start.expiry.help.text.1")}</div>
      <FormTextField
        fieldContainerClass={classes.categoryField}
        name={''}
        id={'key-settings-start-reporting'}
        data-apim-test={'key-settings-start-reporting'}
        variant="standard"
        value={startReporting}
        handleChange={(value) => handleNumberFieldChange( 'startReporting', value)}
        onKeyPress={preventSignsAndFraction}
        error={startReportingError}
        helperText={''}
        textFieldClass={classes.categoryTextField}
        type={'number'}
      />
      <div>
        {intl.getI18nMessage("label.settings.application.keysettings.key.expiry.status.categories.start.expiry.help.text.2")}
        <span className={startReportingError ? classes.errorText : ''}>
          {intl.getI18nMessage("label.settings.application.keysettings.key.expiry.status.categories.start.expiry.help.text.3")}
        </span>
      </div>
    </Fragment>);

  const renderUrgentCategory = () =>
    (<Fragment>
      <div>
        <b>
          {intl.getI18nMessage("label.settings.application.keysettings.key.expiry.status.categories.urgent.expiry.field.label")}
        </b>
        &nbsp;
      </div>
      <div>{intl.getI18nMessage("label.settings.application.keysettings.key.expiry.status.categories.urgent.expiry.help.text.1")}</div>
      <FormTextField
        fieldContainerClass={classes.categoryField}
        name={''}
        id={'key-settings-urgent-reporting'}
        data-apim-test={'key-settings-urgent-reporting'}
        variant="standard"
        value={urgentReporting}
        handleChange={(value) => handleNumberFieldChange('urgentReporting', value )}
        onKeyPress={preventSignsAndFraction}
        error={urgentReportingError}
        helperText={''}
        textFieldClass={classes.categoryTextField}
        type={'number'}
      />
      <div>
        {intl.getI18nMessage("label.settings.application.keysettings.key.expiry.status.categories.urgent.expiry.help.text.2")}
        <span className={urgentReportingError ? classes.errorText : ''}>
          {intl.getI18nMessage("label.settings.application.keysettings.key.expiry.status.categories.urgent.expiry.help.text.3")}
        </span>
      </div>
    </Fragment>);

  const renderStatusCategories = () =>
    (<Fragment>
      <div>
        <Typography variant="h4">
          {intl.getI18nMessage("label.settings.application.keysettings.key.expiry.status.categories.subtitle")}
        </Typography>
      </div>
      <Typography variant="body1">
        {intl.getI18nMessage("label.settings.application.keysettings.key.expiry.status.categories.subtitle.help.text")}
      </Typography>
      <List>
        <ListItem className={classes.categoryListItem}>
          <ListItemIcon
            classes={{
              root: classes.categoryStartItemIcon,
            }}
          >
            <FiberManualRecordSharpIcon
              style={{ fontSize: 16 }}
            />
          </ListItemIcon>
          <ListItemText
            classes={{
              primary: classes.categoryListItemText,
            }}
          >
            {renderStartCategory()}
          </ListItemText>
        </ListItem>
        <ListItem className={classes.categoryListItem}>
          <ListItemIcon
            classes={{
              root: classes.categoryUrgentItemIcon,
            }}
          >
            <FiberManualRecordSharpIcon
              style={{ fontSize: 16 }}
            />
          </ListItemIcon>
          <ListItemText
            classes={{
              primary: classes.categoryListItemText,
            }}
          >
            {renderUrgentCategory()}
          </ListItemText>
        </ListItem>
        <ListItem className={classes.categoryListItem}>
          <ListItemIcon
            classes={{
              root: classes.categoryNoExpiryItemIcon,
            }}
          >
            <FiberManualRecordSharpIcon
              style={{ fontSize: 16 }}
            />
          </ListItemIcon>
          <ListItemText primary={intl.getI18nMessage("label.settings.application.keysettings.key.expiry.status.categories.no.expiry.help.text")} />
        </ListItem>
      </List>
    </Fragment>);

  const disableJobExecutionButton= isFormDirty;

  let reportDayOptions = MONTH_REPORT_DAY_OPTIONS;
  if ((reportCadence === WEEKLY) || (reportCadence === BIWEEKLY)) {
    reportDayOptions = WEEK_REPORT_DAY_OPTIONS;
  } else if (reportCadence === MONTHLY) {
    reportDayOptions = MONTH_REPORT_DAY_OPTIONS;
  } else {
    reportDayOptions = [];
  }

  const getReportInclusionLabelClass = (disabled) => {
    if(disabled)
      return classes.reportInclusionsLabelDisabled;
    if(get(reportIncludes, 'secret') || get(reportIncludes, 'certificate'))
      return '';
    else
    return classes.reportInclusionsLabelError;
  }

  const renderKeyExpiryReports = () =>
    (<Fragment>
      <div className={classes.keysSubtitleContainer}>
        <div>
          <Typography variant="h4">
            {intl.getI18nMessage("label.settings.application.keysettings.key.expiry.reports.subtitle")}
          </Typography>
        </div>
        <div>
          <Switch
            checked={notificationsEnabled}
            id={'key-settings-notifications-enabled'}
            onChange={(checked) => handleInputChange({ target: { name: 'notificationsEnabled', checked } })}
          />
        </div>
      </div>
      <Divider />
      <Typography className={classes.switchHelpText} variant="body1">
        {intl.getI18nMessage("label.settings.application.keysettings.key.expiry.reports.subtitle.helptext")}
      </Typography>
      <Collapse
        in={notificationsEnabled}
        unmountOnExit
      >
        <div className={classes.reportInclusions}>
          <div>
            <Typography variant="body" className={classes.reportContentLabel}>
              {intl.getI18nMessage('label.settings.application.keysettings.key.expiry.reports.includes.contents.title1')}
            </Typography>
            <Typography variant="body" className={getReportInclusionLabelClass()}>
              {intl.getI18nMessage('label.settings.application.keysettings.key.expiry.reports.includes.contents.title2')}
            </Typography>
          </div>
          <div className={classes.inclusionOption}>
            <FormControlLabel
              control={
                <Checkbox
                  data-apim-test="key-settings-report-includes-secret"
                  onChange={(e) => handleReportIncludesChange('secret', e.target.checked)}
                  checked={get(reportIncludes, 'secret', false)}
                  disabled={!expireKeys}
                />
              }
              label={(
                <Typography variant="body" className={getReportInclusionLabelClass(!expireKeys)}>
                  {intl.getI18nMessage('label.settings.application.keysettings.key.expiry.reports.includes.contents.secret')}
                </Typography>)}
            />
          </div>
          <div className={classes.inclusionOption}>
            <FormControlLabel
              control={
                <Checkbox
                  data-apim-test="key-settings-report-includes-certificate"
                  onChange={(e) => handleReportIncludesChange('certificate', e.target.checked)}
                  checked={get(reportIncludes, 'certificate', false)}
                />
              }
              label={(
                <Typography variant="body" className={getReportInclusionLabelClass()}>
                  {intl.getI18nMessage('label.settings.application.keysettings.key.expiry.reports.includes.contents.certificate')}
                </Typography>)}
            />
          </div>
        </div>
        <div className={classes.reportFieldContainer}>
          <FormSelect
            fieldContainerClass={classes.reportField}
            id="key-settings-report-cadence"
            data-apim-test="key-settings-report-cadence"
            data={KEY_EXPIRY_REPORT_CADENCE}
            handleChange={handleCadenceChange}
            name={intl.getI18nMessage("label.settings.application.keysettings.key.expiry.reports.report.cadence")}
            value={reportCadence}
            noNoneOption
            noNativeSelect
          />
          {(reportCadence !== DAILY) && <FormSelect
            fieldContainerClass={classes.reportField}
            id="key-settings-report-day"
            data-apim-test="key-settings-report-day"
            data={reportDayOptions}
            handleChange={(value) => handleInputChange({ target: { name: 'reportDay', value: value } })}
            name={intl.getI18nMessage("label.settings.application.keysettings.key.expiry.reports.report.day")}
            value={reportDay}
            noNoneOption
            noNativeSelect
          />}
        </div>
        <div>
          <Typography variant="body" className={classes.reportContentLabel}>
            {intl.getI18nMessage('label.settings.application.keysettings.key.expiry.reports.report.cadence.send.report.title')}
          </Typography>
        </div>
        <div>
          <FormControlLabel
            control={
              <Checkbox
                name={'sendReportToOrgAdmin'}
                checked={true}
                disabled={true}
              />
            }
            label={intl.getI18nMessage('label.settings.application.keysettings.key.expiry.reports.report.cadence.send.report.to.orgadmin')}
          />
        </div>
        <div>
          <FormControlLabel
            control={
              <Checkbox
                data-apim-test="key-settings-sendReportToOrgPublisher"
                name={'sendReportToOrgPublisher'}
                onChange={handleInputChange}
                checked={sendReportToOrgPublisher}
              />
            }
            label={intl.getI18nMessage('label.settings.application.keysettings.key.expiry.reports.report.cadence.send.report.to.orgpublisher')}
          />
        </div>
        <div>
          <FormControlLabel
            control={
              <Checkbox
                data-apim-test="key-settings-sendReportToAdmin"
                name={'sendReportToAdmin'}
                onChange={handleInputChange}
                checked={sendReportToAdmin}
              />
            }
            label={intl.getI18nMessage('label.settings.application.keysettings.key.expiry.reports.report.cadence.send.report.to.admin')}
          />
        </div>
        {renderReportingJobButton()}
      </Collapse>
    </Fragment>);

  const renderReportingJobButton = () => {
    return (<div className={classes.reportingJobButtonContainer}>
      <div>
        <Tooltip
          arrow
          placement="top"
          title={disableJobExecutionButton ? getI18nFormattedMessage('label.settings.application.keysettings.expire.job.button.tooltip') : ''}
        >
          <span>
            <Button
              className={classes.reportingJobBtn}
              data-apim-test="execute-reporting-job-button"
              disabled={disableJobExecutionButton}
              id={'execute-reporting-job-button'}
              onClick={executeExpiryReportingJob}
              variant="outlined"
              color="primary"
            >
              {intl.getI18nMessage("label.settings.application.keysettings.reporting.job.button.text")}
            </Button>
          </span>
        </Tooltip>
      </div>
      <div
        className={classes.expireJobBtnHelpTextContainer}
      >
        <span
          className={classes.expireJobBtnHelpText}
        >
          {intl.getI18nMessage("label.settings.application.keysettings.reporting.job.button.helptext")}
        </span>
      </div>
    </div>);
  };

  const renderKeyExpiry = () =>
    (<Fragment>
          {renderStatusCategories()}
          {renderKeyExpiryReports()}
          <div className={classes.emptyBlock}/>
    </Fragment>);

  return(
    <div className={classes.details}>
      {(isLoading || isExecutingExpiryJob || isExecutingReportingJob)
        && <Loading pageLoader={true} />}
      {renderKeyExpiry()}
    </div>
  );
};


ExpiryReportingSettings.propTypes = {
 classes: object,
 formValues: object,
 apiKeySettings: object,
 setFormValues: func,
 executeExpiryReportingJob: func,
 isExecutingExpiryJob: bool,
 isExecutingReportingJob: func,
 isLoading: bool,
 urgentReportingError: bool,
 startReportingError: bool,
 isFormDirty: bool,
 handleInputChange: func,
};

export default compose()(ExpiryReportingSettings);
